import React, { type FC } from 'react';
import { FileText, Search } from 'lucide-react';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { safeGet, safeArray, asArray } from './types';

interface AssetItem {
  id?: string;
  title?: string;
  kind?: string;
  mimeType?: string;
}

function kindLabel(kind: string | undefined): string {
  if (!kind) return 'file';
  return kind.toLowerCase();
}

const KindBadge: FC<{ kind?: string }> = ({ kind }) => (
  <span
    style={{
      fontSize: 11,
      color: 'var(--text-secondary)',
      background: 'rgba(0,0,0,0.04)',
      borderRadius: 9999,
      padding: '2px 8px',
      lineHeight: '16px',
      whiteSpace: 'nowrap',
    }}
  >
    {kindLabel(kind)}
  </span>
);

export const AssetCard: FC<ToolCardProps> = ({ toolName, result }) => {
  const isRecall = toolName === 'assetRecall';

  if (isRecall) {
    // assetRecall returns an array of assets
    const assetsFromKey = safeArray<AssetItem>(result, 'assets');
    const assetsFromArray = asArray<AssetItem>(result);
    const assets = (assetsFromKey.length > 0 ? assetsFromKey : assetsFromArray).slice(0, 3);

    return (
      <CardShell>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search
              size={16}
              style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              Related knowledge
            </span>
          </div>

          {/* Asset list */}
          {assets.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {assets.map((asset, i) => (
                <div
                  key={asset.id ?? i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <FileText
                    size={14}
                    style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
                  />
                  <span
                    style={{
                      fontSize: 13,
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {asset.title || 'Untitled'}
                  </span>
                  <KindBadge kind={asset.kind} />
                </div>
              ))}
            </div>
          )}
        </div>
      </CardShell>
    );
  }

  // asset.attach: single asset
  const title =
    safeGet<string>(result, 'title') ?? 'Untitled';
  const kind = safeGet<string>(result, 'kind');

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileText
            size={16}
            style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
          />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {title}
          </span>
        </div>

        {/* Kind + saved label */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            paddingLeft: 24,
          }}
        >
          <KindBadge kind={kind} />
          <span
            style={{
              fontSize: 12,
              color: 'var(--text-tertiary)',
            }}
          >
            Saved to knowledge store
          </span>
        </div>
      </div>
    </CardShell>
  );
};
